// Regras da Solicitação de Adiantamento (Rafael 20/10). Espelho da API em
// accessapi/app/Services/Financeiro/AdiantamentoFeeAgenciaPolicy.php e OrcamentoController::show.
import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';
import { ehSomenteVisualizacao } from './somenteVisualizacaoPolicy';

const nomeNormalizado = (produto = {}) => String(produto?.nome ?? '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase().replace(/\s+/g, ' ').trim();
const codigo = (produto = {}) => String(produto?.codigo ?? '').trim();

// ── 1. FEE da Agência fora do adiantamento ─────────────────────────────────
// FEE da Agência é receita da Access, não conta de fornecedor: 130 FEE AGÊNCIA, 2043/2044
// (Sanofi), "Fee da Agência - ..." (AbbVie 2130-2136), "Fee Agência", e o "Fee Aéreo" do
// 3027 - Serviços Agência (os demais itens do 3027 seguem — Rafael 20/10).
export function ehFeeDaAgencia(produto = {}) {
    if (AgenciaOrcamentoPolicy.isFeeAgenciaGlobal(produto)) return true;
    if (['2043', '2044'].includes(codigo(produto))) return true;
    if (['ACCESS1001', 'ACCESS1002'].includes(String(produto?.codigoFornecedor ?? '').trim().toUpperCase())) return true;
    const nome = nomeNormalizado(produto);
    return nome.includes('FEE AGENCIA') || nome.includes('FEE DA AGENCIA') || nome.includes('FEE AEREO');
}

// Grupo 3028 - FEE sai inteiro: Fee Agência, Imposto, IOF, Outras Taxas (Rafael 20/10).
export function ehGrupoFee(produto = {}) {
    return String(produto?.id ?? '') === '413558481789443599' || codigo(produto) === '3028';
}

// Linha 1945 - Imposto (e a linha virtual da NF Access): imposto da Access, fora do adiantamento em
// qualquer grupo (D-0126, Rafael 28/10).
export function ehLinhaImposto(produto = {}) {
    return ['1945', 'IMPOSTO_NF_ACCESS'].includes(codigo(produto).toUpperCase());
}

// Linha que não aparece em "3 · Itens do orçamento": a API manda `itensForaAdiantamento`
// (grupo 130 - AGÊNCIA, grupo 3028 - FEE, FEE da Agência, Fee Aéreo, 1945 - Imposto e fornecedor
// EBTA); a tela repete o critério pelo catálogo para não depender só da lista. O EBTA (D-0135) não
// está no catálogo: vem só da lista da API, que é o único ponto que conhece o fornecedor.
export function itemForaDoAdiantamento({ subitem = {}, grupo = {}, itensFora = [] } = {}) {
    if ((itensFora || []).map(String).includes(String(subitem?.id ?? ''))) return true;
    // Opção do orçamento (D-0113): só para o cliente ver, não se adianta.
    if (ehSomenteVisualizacao(subitem)) return true;
    if (AgenciaOrcamentoPolicy.isGrupoAgencia({ produto: grupo?.produto || {} })) return true;
    if (ehGrupoFee(grupo?.produto || {})) return true;
    if (subitem?.linhaVirtualImpostoNfAccess || subitem?.linhaCalculadaImpostoAccess) return true;
    if (ehLinhaImposto(subitem?.produto || {})) return true;
    return ehFeeDaAgencia(subitem?.produto || {});
}

// ── 3. Situação: "Creditado" só com o crédito informado pelo Financeiro ────
// `aprovado_diretor` (dataAprovacaoDiretor) também é setado ao ANEXAR O RECIBO: quer dizer
// "liberado para Contas Pendentes", não "o dinheiro caiu". Crédito = status_pagamento.
export function situacaoAdiantamento(adt = {}) {
    if (adt.excluido) return 'excluido';
    if (adt.status_pagamento === 'nao_creditado') return 'nao_creditado';
    if (adt.status_pagamento === 'creditado') return 'creditado';
    if (adt.arquivo_recibo_id) return 'recibo';
    return 'aberto';
}

// Cartão "Já adiantado": soma só os adiantamentos creditados pelo Financeiro (Rafael 20/10).
export function totalCreditado(adiantamentos = []) {
    return (adiantamentos || [])
        .filter((adt) => situacaoAdiantamento(adt) === 'creditado')
        .reduce((soma, adt) => soma + (parseFloat(adt.valor) || 0), 0);
}

// Selo do item na lista da Nova Solicitação (adiantamentosPorItem).
export function seloItemAdiantamento(info = {}) {
    if (!info) return null;
    if (info.nao_creditado) return { texto: '❌ Não creditado — use Renegociar', fundo: '#f8d7da', cor: '#842029' };
    if (info.status_pagamento === 'creditado') return { texto: '✅ Creditado', fundo: '#d1e7dd', cor: '#0f5132' };
    if (info.tem_recibo) return { texto: '📄 Recibo recebido', fundo: '#e7f1ff', cor: '#084298' };
    return { texto: '🔒 Em adiantamento', fundo: '#fff8e1', cor: '#856404' };
}

// ── 4. Valor sugerido ao marcar/desmarcar itens ────────────────────────────
// Regra original (toggle do item): o Valor acompanha a soma dos selecionados (100%) e o % é
// valor ÷ soma; o operador pode digitar um ou outro depois. Complemento: valor livre, não mexe.
// Devolve o novo texto do campo Valor ou `null` para manter o atual.
// 21/10 (Rafael): complemento sugere O QUE FALTA (faltaDoComplemento), limitado à soma dos marcados; sem o dado
// (API antiga) mantém o valor atual.
export function valorPelaSelecao(total, ehComplemento, faltaComplemento = null) {
    if (ehComplemento) {
        if (faltaComplemento === null || faltaComplemento === undefined) return null;
        const sugestao = Math.max(0, Math.min(Number(total) || 0, Number(faltaComplemento) || 0));
        return sugestao > 0 ? sugestao.toFixed(2).replace('.', ',') : '';
    }
    const soma = Number(total) || 0;
    return soma > 0 ? soma.toFixed(2).replace('.', ',') : '';
}
